// The two derived single-variable H3 views for the map lab (`/_/map-lab`).
//
// The operator endorsed the hexagon grid and found that a cell made to carry
// several encodings has room for none of them. Both views below answer that by
// carrying ONE variable per cell on ONE sequential ramp — coverage the share
// treated, status the count actionable now — so a cell means exactly one thing
// and reads at a glance.
//
// Each view owns its tegola map, its parameters, its ramp and its rail and key
// readings, all from one definition, so the pane (paint), the legend (key) and
// the click rail describe the same encoding instead of three near-copies that
// drift. It is pure (no Vue, no store, no map) for the same reason `multi.ts`
// is: the names and the numbers must not live in more than one place.
//
// Both maps are the SAME shape `h3-multi` is — one feature per H3 cell
// carrying the cell's own counts — so these views are a repaint of a grid the
// lab already knows how to draw, not a new encoding family.

export type DerivedViewID = "coverage" | "status";
export type DerivedModeID = "hex-coverage" | "hex-status";

/** The tile's per-cell counts, in the maps' own spelling. `n_places` is the
 * cell's whole place universe — the coverage denominator and the honest base
 * every rail reading is read against. */
const FIELD_PLACES = "n_places";
const FIELD_TREATED = "n_treated";
const FIELD_ACTIVE = "n_active";
const FIELD_NEEDS = "n_needs";
const FIELD_PRODUCTION = "n_production";
const FIELD_DUE = "n_due";

/** Blues and Reds — the two conventional workbench ramps: coverage is a share,
 * so it reads cool and light → dark; status is work waiting, so it reads warm. */
export const COVERAGE_RAMP = [
	"#f7fbff",
	"#c6dbef",
	"#6baed6",
	"#2171b5",
	"#08306b",
];
export const STATUS_RAMP = [
	"#fff5f0",
	"#fcbba1",
	"#fb6a4a",
	"#cb181d",
	"#67000d",
];

/** Coverage is a share of five quarter marks. Status is a small count whose 5
 * stop absorbs the long tail — org 1 peaks at 1 actionable place per cell and
 * org 124 at 4, so the top of the ramp is where "more" stops resolving. */
const COVERAGE_STOPS = [0, 0.25, 0.5, 0.75, 1];
const STATUS_STOPS = [0, 1, 2, 3, 5];

/** The stops each view's ramp is pinned to, keyed by view so the fill and the
 * key read one table rather than a branch each. */
const RAMP_STOPS: Record<DerivedViewID, number[]> = {
	coverage: COVERAGE_STOPS,
	status: STATUS_STOPS,
};

/** The three tegola names each view owns, kept beside the view so the URL
 * builder and the pane cannot disagree about which map a mode reads. */
export const DERIVED_MAP_COVERAGE = "h3-coverage";
export const DERIVED_LAYER_COVERAGE = "service-area-h3-coverage";
export const DERIVED_MAP_STATUS = "h3-status";
export const DERIVED_LAYER_STATUS = "service-area-h3-status";

/** One derived view: the single variable a cell carries and everything needed
 * to draw, key and read it. `mode` is the lab's mode id; `label` and `blurb`
 * are that mode row's words. */
export interface DerivedView {
	id: DerivedViewID;
	mode: DerivedModeID;
	/** The mode row's label. */
	label: string;
	/** The mode row's blurb. */
	blurb: string;
	/** ONE line: what a cell of this view means. */
	cellMeans: string;
	/** tegola map name. */
	map: string;
	/** MVT source-layer name. */
	sourceLayer: string;
	/** The tile parameters, in tilde order; `extra` is the view's own knob
	 * (coverage's active-only narrowing, status's revisit horizon). */
	params: {
		resolution: string;
		from: string;
		to: string;
		extra: string;
	};
	/** The ramp the fill and the key both read, light → dark. */
	ramp: string[];
	/** The field the ramp's top of scale reads ("n_treated" / "n_needs"). */
	maxField: string;
}

export const DERIVED_VIEWS: DerivedView[] = [
	{
		id: "coverage",
		mode: "hex-coverage",
		label: "Hex coverage — treated share",
		blurb:
			"one hue per cell on a sequential ramp: the share of the cell's places treated in the window.",
		cellMeans:
			"Share of the cell's Features that had at least one live treatment in the window.",
		map: DERIVED_MAP_COVERAGE,
		sourceLayer: DERIVED_LAYER_COVERAGE,
		params: {
			resolution: "h3_coverage_resolution",
			from: "h3_coverage_from",
			to: "h3_coverage_to",
			extra: "h3_coverage_active_only",
		},
		ramp: COVERAGE_RAMP,
		maxField: FIELD_TREATED,
	},
	{
		id: "status",
		mode: "hex-status",
		label: "Hex status — actionable now",
		blurb:
			"one hue per cell on a sequential ramp: how many of the cell's places are actionable now.",
		cellMeans:
			"Count of the cell's Features whose latest inspection found production and which have not been treated since, plus those due for a revisit.",
		map: DERIVED_MAP_STATUS,
		sourceLayer: DERIVED_LAYER_STATUS,
		params: {
			resolution: "h3_status_resolution",
			from: "h3_status_from",
			to: "h3_status_to",
			extra: "h3_status_horizon_days",
		},
		ramp: STATUS_RAMP,
		maxField: FIELD_NEEDS,
	},
];

export function derivedByID(id: DerivedViewID): DerivedView {
	return DERIVED_VIEWS.find((view) => view.id === id) ?? DERIVED_VIEWS[0];
}

export function derivedViewForMode(mode: string): DerivedView | null {
	return DERIVED_VIEWS.find((view) => view.mode === mode) ?? null;
}

export function isDerivedModeID(value: string): value is DerivedModeID {
	return DERIVED_VIEWS.some((view) => view.mode === value);
}

/** The default status horizon, mirroring the server's `DUE_SOON_DEFAULT_DAYS`:
 * the parameter is sent only when it differs, so the standing request is the
 * map's own default and a change of horizon is a visible URL change. */
const STATUS_HORIZON_DEFAULT = 5;

export interface DerivedTileInput {
	view: DerivedView;
	base: string;
	resolution: number;
	from: string;
	to: string;
	/** Coverage only: narrow the universe to active Features. */
	activeOnly?: boolean;
	/** Status only: the revisit horizon in days. */
	horizonDays?: number;
}

/**
 * The derived grid's tile URL for one organisation, resolution and window.
 *
 * Same shape as `multiTileTiles`: one template per view, so switching between
 * the two derived modes hands MapLibre a different source and re-fetches, and
 * switching back to a source it already holds is free. The window bounds are
 * optional and an absent one is unbounded to the server, so a pane with no
 * window sends no date parameters at all.
 */
export function derivedTileTiles(input: DerivedTileInput): string[] {
	if (!input.base) return [];
	const { params, map } = input.view;
	const search = new URLSearchParams();
	search.set(params.resolution, String(input.resolution));
	if (input.from) search.set(params.from, input.from);
	if (input.to) search.set(params.to, input.to);
	if (input.view.id === "coverage") {
		// The safe default is the whole universe, so only the narrowing has to
		// travel; a default 0 the server already assumes would be noise.
		if (input.activeOnly) search.set(params.extra, "1");
	} else if (
		input.horizonDays !== undefined &&
		input.horizonDays !== STATUS_HORIZON_DEFAULT
	) {
		search.set(params.extra, String(input.horizonDays));
	}
	return [`${input.base}maps/${map}/{z}/{x}/{y}?${search}`];
}

// ── Paint ──────────────────────────────────────────────────────────────────
//
// The fill is a MapLibre `interpolate` over the tile's own property, so the
// mode is a style update on the grid MapLibre already holds — no new tiles.

/** The ramp laid over the cell's variable: an `interpolate` array of
 * alternating stop and colour, the shape MapLibre reads. The value is coverage's
 * treated fraction of the cell's places, or status's raw count — whose handful
 * of values is its own axis. */
export function derivedFillColor(view: DerivedView): unknown[] {
	const input =
		view.id === "coverage"
			? ["/", ["get", view.maxField], ["get", FIELD_PLACES]]
			: ["get", view.maxField];
	const expression: unknown[] = ["interpolate", ["linear"], input];
	RAMP_STOPS[view.id].forEach((stop, index) => {
		expression.push(stop, view.ramp[index]);
	});
	return expression;
}

// ── The click rail ─────────────────────────────────────────────────────────

export interface DerivedRow {
	label: string;
	value: string;
}

export interface DerivedReading {
	headline: string;
	rows: DerivedRow[];
}

/** What an absent property reads as. A field the tile did not carry is an
 * honest blank, never `NaN` in the rail. */
const BLANK = "—";

function tileNumber(
	properties: Record<string, unknown>,
	field: string,
): number | null {
	const value = properties[field];
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function countText(value: number | null): string {
	return value === null ? BLANK : String(value);
}

/** The rounded treated share — the cell's headline and the number the ramp
 * places it at. */
function coverageHeadline(properties: Record<string, unknown>): string {
	const places = tileNumber(properties, FIELD_PLACES);
	const treated = tileNumber(properties, FIELD_TREATED);
	if (places === null || treated === null || places <= 0) {
		return `${BLANK} treated`;
	}
	return `${Math.round((treated / places) * 100)}% treated`;
}

/** The on-click per-cell composition, read out of the tile feature's own
 * properties — the derived analogue of `cellCounts`, with the extra components
 * the maps report so the rail can say what the one variable is made of. */
export function derivedCellReading(
	view: DerivedView,
	properties: Record<string, unknown>,
): DerivedReading {
	if (view.id === "coverage") {
		return {
			headline: coverageHeadline(properties),
			rows: [
				{
					label: "Places in cell",
					value: countText(tileNumber(properties, FIELD_PLACES)),
				},
				{
					label: "Treated in window",
					value: countText(tileNumber(properties, FIELD_TREATED)),
				},
				{
					label: "Active places",
					value: countText(tileNumber(properties, FIELD_ACTIVE)),
				},
			],
		};
	}
	return {
		headline: `${countText(tileNumber(properties, FIELD_NEEDS))} actionable now`,
		rows: [
			{
				label: "Places in cell",
				value: countText(tileNumber(properties, FIELD_PLACES)),
			},
			{
				label: "Production, untreated",
				value: countText(tileNumber(properties, FIELD_PRODUCTION)),
			},
			{
				label: "Due for revisit",
				value: countText(tileNumber(properties, FIELD_DUE)),
			},
		],
	};
}

// ── Legends ────────────────────────────────────────────────────────────────

export interface DerivedLegendTick {
	value: number;
	label: string;
	color: string;
}

export interface DerivedLegend {
	title: string;
	note: string;
	ticks: DerivedLegendTick[];
}

/** The tick's printed value: coverage reads as a percentage, status as its
 * count, and the top stop of the count ramp as "5+" because the fill clamps
 * there. */
function tickLabel(view: DerivedView, value: number): string {
	if (view.id === "coverage") return `${Math.round(value * 100)}%`;
	const top = STATUS_STOPS[STATUS_STOPS.length - 1];
	return value === top ? `${value}+` : String(value);
}

export function derivedLegend(view: DerivedView): DerivedLegend {
	return {
		title: view.label,
		note: view.cellMeans,
		ticks: RAMP_STOPS[view.id].map((value, index) => ({
			value,
			label: tickLabel(view, value),
			color: view.ramp[index],
		})),
	};
}
